import { useState, useEffect } from 'react';
import { Video, FileText, User, CheckCircle, Clock, XCircle, Upload, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const STEPS = [
  { id: 'business', icon: FileText, label: 'Business Details' },
  { id: 'documents', icon: Upload, label: 'Documents' },
  { id: 'videos', icon: Video, label: 'Verification Videos' },
  { id: 'review', icon: CheckCircle, label: 'Submit for Review' },
];

export default function SellerKYB() {
  const [kybStatus, setKybStatus] = useState('none');
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    businessName: '', businessType: '', businessCountry: '',
    businessAddress: '', registrationNumber: '',
  });
  const [files, setFiles] = useState({ businessDoc: null, faceVideo: null, productVideo: null });

  useEffect(() => {
    api.get('/kyb/status').then(r => setKybStatus(r.data.kyb?.status || 'none')).catch(() => {});
  }, []);

  const setFile = (key, file) => setFiles(f => ({ ...f, [key]: file }));

  const handleSubmit = async () => {
    if (!form.businessName || !form.businessType || !form.businessCountry) {
      return toast.error('Fill in all business details');
    }
    if (!files.faceVideo || !files.productVideo) {
      return toast.error('Both face video and product video are required');
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (files.businessDoc) fd.append('businessDoc', files.businessDoc);
      fd.append('faceVideo', files.faceVideo);
      fd.append('productVideo', files.productVideo);

      await api.post('/kyb/submit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('KYB submitted! We\'ll review within 24-48 hours.');
      setKybStatus('pending');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  // Status screens
  if (kybStatus === 'approved') return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-lg mx-auto flex flex-col items-center justify-center">
      <div className="text-center glass rounded-2xl p-8">
        <CheckCircle size={56} className="text-green-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Sora' }}>KYB Approved! ✅</h2>
        <p className="text-brand-muted mb-6">Your business is verified. You can now create your storefront and list products.</p>
        <a href="/profile" className="btn-primary inline-block px-8">Set Up My Store →</a>
      </div>
    </div>
  );

  if (kybStatus === 'pending') return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-lg mx-auto flex flex-col items-center justify-center">
      <div className="text-center glass rounded-2xl p-8">
        <Clock size={56} className="text-yellow-400 mx-auto mb-4 pulse-orange" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Sora' }}>Under Review</h2>
        <p className="text-brand-muted">Your KYB application is being reviewed. This usually takes 24–48 hours. We'll notify you by email.</p>
      </div>
    </div>
  );

  if (kybStatus === 'rejected') return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-lg mx-auto flex flex-col items-center justify-center">
      <div className="text-center glass rounded-2xl p-8">
        <XCircle size={56} className="text-red-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Sora' }}>Application Rejected</h2>
        <p className="text-brand-muted mb-4">Your KYB was not approved. You may resubmit with correct information.</p>
        <button onClick={() => setKybStatus('none')} className="btn-primary px-8">Resubmit KYB</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-2xl mx-auto">
      <div className="mt-4 mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Sora' }}>Seller Verification (KYB)</h1>
        <p className="text-brand-muted text-sm mt-1">Complete verification to start selling on CryptoMart</p>
      </div>

      {/* Warning */}
      <div className="flex items-start gap-3 p-4 rounded-xl mb-6" style={{ background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.25)' }}>
        <AlertTriangle size={18} className="text-yellow-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-yellow-200">To protect buyers from scammers, all sellers must complete KYB. Your videos will be reviewed by our team and never shared publicly.</p>
      </div>

      {/* Step indicators */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-1">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-1 flex-shrink-0">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                style={{
                  background: i <= step ? 'linear-gradient(135deg,#FF6B00,#F5A623)' : '#12121A',
                  color: i <= step ? 'white' : '#6B6B8A',
                  border: i <= step ? 'none' : '1px solid #1E1E2E',
                }}>
                {i < step ? <CheckCircle size={14} /> : i + 1}
              </div>
              <span className="text-xs text-brand-muted mt-1 hidden sm:block">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className="w-8 h-0.5 flex-shrink-0" style={{ background: i < step ? '#FF6B00' : '#1E1E2E' }} />}
          </div>
        ))}
      </div>

      <div className="glass rounded-2xl p-6">
        {/* Step 0: Business Details */}
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Sora' }}>Business Details</h2>
            <input className="input-dark" placeholder="Business / Store Name *" required
              value={form.businessName} onChange={e => setForm({ ...form, businessName: e.target.value })} />
            <select className="input-dark" value={form.businessType} onChange={e => setForm({ ...form, businessType: e.target.value })}>
              <option value="">Business Type *</option>
              <option value="individual">Individual / Sole Trader</option>
              <option value="registered">Registered Business</option>
              <option value="manufacturer">Manufacturer</option>
              <option value="retailer">Retailer</option>
              <option value="dropshipper">Dropshipper</option>
            </select>
            <input className="input-dark" placeholder="Country of Business *"
              value={form.businessCountry} onChange={e => setForm({ ...form, businessCountry: e.target.value })} />
            <input className="input-dark" placeholder="Business Address"
              value={form.businessAddress} onChange={e => setForm({ ...form, businessAddress: e.target.value })} />
            <input className="input-dark" placeholder="Registration Number (if registered)"
              value={form.registrationNumber} onChange={e => setForm({ ...form, registrationNumber: e.target.value })} />
            <button onClick={() => setStep(1)} disabled={!form.businessName || !form.businessType || !form.businessCountry}
              className="btn-primary w-full disabled:opacity-40">
              Next: Upload Documents →
            </button>
          </div>
        )}

        {/* Step 1: Documents */}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Sora' }}>Business Documents</h2>
            <p className="text-brand-muted text-sm">Upload your business registration, license, or any official document (optional but recommended).</p>
            <label className="block">
              <div className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:border-brand-orange"
                style={{ borderColor: files.businessDoc ? '#FF6B00' : '#1E1E2E', background: '#0A0A0F' }}>
                <Upload size={24} className="mx-auto mb-2 text-brand-muted" />
                <p className="text-sm text-brand-muted">{files.businessDoc ? files.businessDoc.name : 'Click to upload business document (PDF/Image)'}</p>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => setFile('businessDoc', e.target.files[0])} />
              </div>
            </label>
            <div className="flex gap-2">
              <button onClick={() => setStep(0)} className="btn-outline flex-1 py-2 text-sm">← Back</button>
              <button onClick={() => setStep(2)} className="btn-primary flex-1 py-2 text-sm">Next: Videos →</button>
            </div>
          </div>
        )}

        {/* Step 2: Videos */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Sora' }}>Verification Videos</h2>
            <div className="p-3 rounded-xl text-sm" style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.2)', color: '#E0E0F0' }}>
              <strong className="text-brand-orange">Important:</strong> Record two short videos:
              <ol className="mt-2 space-y-1 text-brand-muted list-decimal ml-4">
                <li><strong className="text-white">Face Video:</strong> Show your face clearly, say your name and business name</li>
                <li><strong className="text-white">Product Video:</strong> Show the products you sell, clearly demonstrating what you're offering</li>
              </ol>
            </div>

            {[
              { key: 'faceVideo', label: 'Face Video *', desc: 'Show your face + state your name and business name', required: true },
              { key: 'productVideo', label: 'Product Video *', desc: 'Show what products you sell', required: true },
            ].map(({ key, label, desc, required }) => (
              <div key={key}>
                <p className="text-sm font-semibold mb-1">{label}</p>
                <p className="text-xs text-brand-muted mb-2">{desc}</p>
                <label className="block">
                  <div className="border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-colors hover:border-brand-orange"
                    style={{ borderColor: files[key] ? '#FF6B00' : '#1E1E2E', background: '#0A0A0F' }}>
                    <Video size={22} className="mx-auto mb-1" style={{ color: files[key] ? '#FF6B00' : '#6B6B8A' }} />
                    <p className="text-xs text-brand-muted">{files[key] ? `✅ ${files[key].name}` : 'Tap to upload video (MP4/WebM, max 100MB)'}</p>
                    <input type="file" accept="video/*" className="hidden" onChange={e => setFile(key, e.target.files[0])} />
                  </div>
                </label>
              </div>
            ))}

            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="btn-outline flex-1 py-2 text-sm">← Back</button>
              <button onClick={() => setStep(3)} disabled={!files.faceVideo || !files.productVideo}
                className="btn-primary flex-1 py-2 text-sm disabled:opacity-40">
                Review & Submit →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-bold text-lg" style={{ fontFamily: 'Sora' }}>Review & Submit</h2>
            <div className="space-y-2">
              {[
                ['Business Name', form.businessName],
                ['Business Type', form.businessType],
                ['Country', form.businessCountry],
                ['Face Video', files.faceVideo?.name || '—'],
                ['Product Video', files.productVideo?.name || '—'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between text-sm py-2" style={{ borderBottom: '1px solid #1E1E2E' }}>
                  <span className="text-brand-muted">{label}</span>
                  <span className="text-brand-text font-medium truncate max-w-[180px]">{value}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-brand-muted">By submitting, you confirm all information is accurate. False information will result in permanent ban.</p>
            <div className="flex gap-2">
              <button onClick={() => setStep(2)} className="btn-outline flex-1 py-2 text-sm">← Back</button>
              <button onClick={handleSubmit} disabled={loading} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {loading ? <div className="spinner" /> : '🚀 Submit KYB'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
